import React, { useEffect, useRef } from 'react';

/**
 * A full-page night sky for the sign-in screen: several hundred small stars
 * in three depth layers that twinkle and drift very slowly, with the odd
 * shooting star. It sits behind everything -- form included -- and is dimmed
 * over the form column so text stays perfectly readable.
 *
 * With reduced motion it paints one still frame.
 */

const COLORS = ['#ffffff', '#ffffff', '#d6e4ff', '#a9c2ff', '#ffe8bd', '#ffc9c9'];

function makeSprite(color, radiusCss, dpr) {
  const d = Math.max(4, Math.ceil(radiusCss * 2 * dpr));
  const c = document.createElement('canvas');
  c.width = d;
  c.height = d;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(d / 2, d / 2, 0, d / 2, d / 2, d / 2);
  grad.addColorStop(0, color);
  grad.addColorStop(0.3, color);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, d, d);
  return c;
}

export default function StarField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let W = 0, H = 0, dpr = 1, raf = 0;
    let stars = [];
    let sprites = [];
    let shoot = null;
    let nextShoot = 5000 + Math.random() * 4000;
    let started = 0;

    function build() {
      W = window.innerWidth;
      H = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      sprites = COLORS.map((c) => makeSprite(c, 2, dpr));

      const count = Math.round(Math.min(480, Math.max(90, (W * H) / 4200)));
      stars = [];
      for (let i = 0; i < count; i += 1) {
        const z = [0.45, 0.75, 1][i % 3];                     // depth layer
        const bright = Math.pow(Math.random(), 4);            // a few bright ones
        stars.push({
          x: Math.random() * W,
          y: Math.random() * H,
          z,
          r: (0.55 + bright * 1.5) * (0.7 + 0.3 * z),
          a: (0.3 + Math.random() * 0.6) * (0.55 + 0.45 * z),
          tw: 0.0006 + Math.random() * 0.0024,
          ph: Math.random() * 6.283,
          c: Math.floor(Math.random() * COLORS.length),
          dx: (0.006 + Math.random() * 0.02) * z,
          dy: (Math.random() - 0.5) * 0.008 * z,
          halo: bright > 0.25,
        });
      }
    }

    // Dim the sky over the form column so labels and hints stay crisp.
    function mask(x) {
      if (W < 900) return 0.8;
      const f = Math.min(1, Math.max(0, (x / W - 0.24) / 0.28));
      return 0.5 + 0.5 * (f * f * (3 - 2 * f));
    }

    function drawStars(now) {
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';
      for (let i = 0; i < stars.length; i += 1) {
        const s = stars[i];
        if (!reduce) {
          s.x += s.dx;
          s.y += s.dy;
          if (s.x > W + 6) s.x = -6;
          if (s.y < -6) s.y = H + 6; else if (s.y > H + 6) s.y = -6;
        }
        const tw = reduce ? 0.8 : 0.55 + 0.45 * Math.sin(now * s.tw + s.ph);
        const a = s.a * tw * mask(s.x);
        const d = s.r * 3.4;
        ctx.globalAlpha = a;
        ctx.drawImage(sprites[s.c], s.x - d, s.y - d, d * 2, d * 2);
        if (s.halo) {
          const h = s.r * 9;
          ctx.globalAlpha = a * 0.16;
          ctx.drawImage(sprites[s.c], s.x - h, s.y - h, h * 2, h * 2);
        }
      }
      ctx.globalAlpha = 1;
    }

    function drawShoot(now) {
      if (reduce) return;
      if (!shoot && now - started > nextShoot) {
        // Streaks across the upper sky, heading down-left or down-right.
        const dir = Math.random() < 0.5 ? 1 : -1;
        const slope = 0.3 + Math.random() * 0.25;
        shoot = {
          x: W * (0.15 + Math.random() * 0.7),
          y: H * (0.05 + Math.random() * 0.35),
          vx: 9 * dir,
          vy: 9 * slope,
          born: now,
          len: 90 + Math.random() * 80,
        };
        nextShoot = now - started + 7000 + Math.random() * 6000;
      }
      if (!shoot) return;
      const life = (now - shoot.born) / 900;
      if (life >= 1) { shoot = null; return; }
      shoot.x += shoot.vx;
      shoot.y += shoot.vy;
      const fade = Math.sin(Math.PI * life);
      const mag = Math.hypot(shoot.vx, shoot.vy);
      const tx = shoot.x - (shoot.vx / mag) * shoot.len;
      const ty = shoot.y - (shoot.vy / mag) * shoot.len;
      const g = ctx.createLinearGradient(shoot.x, shoot.y, tx, ty);
      g.addColorStop(0, `rgba(255,255,255,${0.85 * fade * mask(shoot.x)})`);
      g.addColorStop(1, 'rgba(160,190,255,0)');
      ctx.globalAlpha = 1;
      ctx.strokeStyle = g;
      ctx.lineWidth = 1.2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(shoot.x, shoot.y);
      ctx.lineTo(tx, ty);
      ctx.stroke();
    }

    function frame(now) {
      raf = requestAnimationFrame(frame);
      if (!started) started = now;
      drawStars(now);
      drawShoot(now);
    }

    build();
    if (reduce) {
      drawStars(0);
    } else {
      raf = requestAnimationFrame(frame);
    }

    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { build(); if (reduce) drawStars(0); }, 120);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return <canvas ref={ref} className="auth-stars" aria-hidden="true" />;
}
